import React, { useEffect, useRef, useState } from 'react';
import type { PageSettings, Problem } from './api';
import { EditIcon, PlusIcon, TrashIcon } from './icons';
import { useImpression } from './track';

export type EditMode = 'view' | 'new' | 'edit';

interface Props {
  problems: Problem[];
  settings: PageSettings;
  active: Problem | null;
  onSelect: (p: Problem) => void;
  mode: EditMode;
  setMode: (m: EditMode) => void;
  /** Resolves true when saved (the parent leaves edit mode). */
  onSave: (text: string) => Promise<boolean>;
  onRemove: () => void;
  busy: boolean;
  error: string;
}

const ProblemsSection: React.FC<Props> = ({ problems, settings, active, onSelect, mode, setMode, onSave, onRemove,
                                            busy, error }) => {
  const ref = useImpression<HTMLElement>('problems');
  const [draft, setDraft] = useState('');
  const box = useRef<HTMLTextAreaElement | null>(null);
  const max = settings.max_problems;
  const maxChars = settings.max_chars;
  const editing = mode !== 'view';
  const atCap = problems.length >= max;

  useEffect(() => {
    if (mode === 'new') setDraft('');
    if (mode === 'edit') setDraft(active?.text || '');
    if (editing) setTimeout(() => box.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const startNew = () => { if (!editing && !atCap) setMode('new'); };
  const canSave = draft.trim().length > 0 && draft.length <= maxChars && !busy;

  return (
    <section ref={ref} className="fy-sec" aria-label="Your problem statements">
      <div className="fy-sec-head">
        <h2 className="fy-h2">Your problem statements</h2>
        {(problems.length > 0 || editing) && (
          <button type="button" className="fy-btn fy-btn-outline" onClick={startNew} disabled={editing || atCap}
                  title={atCap ? `You can have up to ${max} problem statements.` : undefined}>
            <PlusIcon /> Add problem statement
          </button>
        )}
      </div>

      {atCap && !editing && (
        <div className="fy-muted">You can have up to {max} problem statements. Remove one to add another.</div>
      )}

      {problems.length > 0 && (
        <div className="fy-tabs" role="group" aria-label="Choose a problem statement">
          {problems.map((p, i) => (
            <button key={p.id} type="button" className="fy-tab" aria-pressed={active?.id === p.id}
                    disabled={editing && active?.id !== p.id} onClick={() => { if (!editing) onSelect(p); }}
                    title={p.text}>
              <span className="fy-tab-num">Problem {i + 1}</span>
              <span className="fy-tab-text">{p.text}</span>
            </button>
          ))}
        </div>
      )}

      {!editing && active && (
        <div className="fy-focus">
          <div className="fy-kicker">You're exploring solutions for</div>
          <p className="fy-focus-q">“{active.text}”</p>
          <div className="fy-row">
            <button type="button" className="fy-btn fy-btn-quiet fy-btn-sm" onClick={() => setMode('edit')} disabled={busy}>
              <EditIcon /> Edit
            </button>
            <button type="button" className="fy-btn fy-btn-quiet fy-btn-sm" onClick={onRemove} disabled={busy}>
              <TrashIcon /> Remove
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="fy-focus">
          <label className="fy-label" htmlFor="fy-stmt-draft">
            {mode === 'new' ? 'New problem statement' : 'Edit problem statement'}
          </label>
          <textarea id="fy-stmt-draft" ref={box} className="fy-textarea" rows={3} maxLength={maxChars} value={draft}
                    onChange={e => setDraft(e.target.value.slice(0, maxChars))}
                    onKeyDown={e => { if (e.key === 'Escape') setMode('view'); }}
                    placeholder="e.g. How can we cut cold-chain losses for vaccines in last-mile delivery?" />
          <div className="fy-row-end">
            <div className="fy-muted">
              Name the problem, the setting and the outcome you want.{' '}
              <span aria-live="polite">{draft.length}/{maxChars}</span>
            </div>
            <div className="fy-row">
              <button type="button" className="fy-btn fy-btn-quiet" onClick={() => setMode('view')} disabled={busy}>Cancel</button>
              <button type="button" className="fy-btn fy-btn-primary" disabled={!canSave}
                      onClick={async () => { await onSave(draft.trim()); }}>
                {busy ? 'Saving…' : mode === 'new' ? 'Add statement' : 'Save changes'}
              </button>
            </div>
          </div>
          {error && <div className="fy-err" role="alert">{error}</div>}
        </div>
      )}

      {!editing && !active && (
        <div className="fy-empty">
          <div className="fy-empty-title">Tell us the problem you're solving</div>
          <p className="fy-muted">Add a problem statement and Cosmos will recommend university technologies that address it.</p>
          <button type="button" className="fy-btn fy-btn-primary" onClick={startNew}>
            <PlusIcon /> Add problem statement
          </button>
        </div>
      )}
      {!editing && error && <div className="fy-err" role="alert">{error}</div>}
    </section>
  );
};

export default ProblemsSection;
